/**
 * Order Calculation Utilities
 * 
 * Functions for calculating optimal order values when inserting elements
 * at specific positions without requiring full reordering of existing elements.
 */

/**
 * Calculates the optimal component order value for insertion at a specific position.
 * Handles edge cases: empty list, before first, after last, and between elements.
 * Uses fractional orders to insert between existing components without reordering.
 * 
 * @param {number} requestedPosition - The position index where the new component should appear (0-based)
 * @param {Array} existingComponents - Array of existing components with order property
 * @returns {number} The calculated order value for the new component
 * 
 * @example
 * // Empty page
 * calculateComponentOrder(0, []) // => 0
 * 
 * @example
 * // Before first component
 * calculateComponentOrder(0, [{ order: 0 }, { order: 1 }]) // => -1
 * 
 * @example
 * // After last component
 * calculateComponentOrder(3, [{ order: 0 }, { order: 1 }, { order: 2 }]) // => 3
 * 
 * @example
 * // Between two components with gap
 * calculateComponentOrder(2, [{ order: 0 }, { order: 1 }, { order: 3 }, { order: 4 }]) // => 2
 * 
 * @example
 * // Between adjacent components (fractional midpoint)
 * calculateComponentOrder(2, [{ order: 0 }, { order: 1 }, { order: 2 }]) // => 1.5
 */
export const calculateComponentOrder = (requestedPosition, existingComponents) => {
  // Ensure existingComponents is sorted by order for predictable behavior
  const sortedComponents = [...existingComponents].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  
  // Case 1: Empty page - start at 0
  if (sortedComponents.length === 0) {
    return 0;
  }
  
  // Case 2: Insert before first component
  if (requestedPosition === 0) {
    const firstOrder = sortedComponents[0]?.order ?? 0;
    return firstOrder - 1;
  }
  
  // Case 3: Insert after last component
  if (requestedPosition >= sortedComponents.length) {
    const lastOrder = sortedComponents[sortedComponents.length - 1]?.order ?? 0;
    return lastOrder + 1;
  }
  
  // Case 4: Insert between two components
  const prevComponent = sortedComponents[requestedPosition - 1];
  const nextComponent = sortedComponents[requestedPosition];
  
  const prevOrder = prevComponent?.order ?? 0;
  const nextOrder = nextComponent?.order ?? 0;
  
  if (prevOrder < nextOrder) {
    // There's space between them - use midpoint for clean insertion
    return (prevOrder + nextOrder) / 2;
  } else {
    // Orders are same or inverted (edge case) - insert right after previous
    return prevOrder + 0.5;
  }
};




